import React, { useState, useRef, useEffect, useCallback } from 'react'
import { hasKey, setKey } from 'api/apiKey'
import { changePrompt } from 'actions/prompt'
import { requestGraphQuery, closeGraphQueryModal } from 'actions/graphQuery'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import selectPrompt from 'selectors/prompt'
import selectGraphQuery from 'selectors/graphQuery'
import Button from 'components/utils/Button'
import Modal from 'components/utils/Modal'
import FormControl from 'components/utils/FormControl'
import 'components/GraphQueryModal.less'

type InputEventData = {
  target?: {
    value?: string
  }
}

type KeyboardEventData = {
  key?: string
  shiftKey?: boolean
  preventDefault?: () => void
  stopPropagation?: () => void
}

type GraphQueryData = {
  isFetching?: boolean
  showModal?: boolean
}

const GraphQueryModal = () => {
  const dispatch = useAppDispatch()
  const prompt = useAppSelector((state) => selectPrompt(state as any) as string)
  const graphQuery = useAppSelector((state) => selectGraphQuery(state as any) as GraphQueryData)

  const [apiKey, setApiKey] = useState('')
  const keyInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const promptInputRef = useRef<HTMLTextAreaElement | null>(null)
  const prevShowModal = useRef(false)

  useEffect(() => {
    const wasClosedNowOpen = !prevShowModal.current && !!graphQuery?.showModal
    prevShowModal.current = !!graphQuery?.showModal

    if (wasClosedNowOpen) {
      if (hasKey()) {
        setTimeout(() => promptInputRef.current?.focus?.(), 0)
      } else {
        setTimeout(() => keyInputRef.current?.focus?.(), 0)
      }
    }
  }, [graphQuery?.showModal])

  const closeModal = useCallback(() => {
    dispatch(closeGraphQueryModal())
  }, [dispatch])

  const confirmAsk = useCallback(() => {
    if (graphQuery?.isFetching) return
    if (!hasKey()) {
      setKey(apiKey)
      if (!apiKey?.trim()?.length) {
        keyInputRef.current?.focus?.()
        return
      }
    }
    if (!prompt?.trim()?.length) {
      promptInputRef.current?.focus?.()
      return
    }
    dispatch(requestGraphQuery())
  }, [graphQuery?.isFetching, apiKey, prompt, dispatch])

  return (
    <Modal
      open={!!graphQuery?.showModal}
      onClose={closeModal}
      className="graph-query-modal"
    >
      <div className="modal-body">
        {!hasKey() ? (
          <div>
            <FormControl
              type="password"
              value={apiKey || ''}
              onChange={(e: unknown) => {
                const value = ((e as InputEventData).target?.value || '')
                setApiKey(value)
              }}
              placeholder="Anthropic API key"
              disabled={graphQuery?.isFetching}
              ref={(el: HTMLInputElement | null) => (keyInputRef.current = el)}
              name="anthropic_api_key"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={(e: unknown) => {
                const ke = e as KeyboardEventData
                if (ke.key === 'Escape') {
                  closeModal()
                }
                if (ke.key === 'Enter') {
                  ke.preventDefault?.()
                  ke.stopPropagation?.()
                }
              }}
            />
            <div style={{ height: '0.75em' }} />
          </div>
        ) : null}
        <FormControl
          as="textarea"
          value={prompt}
          onChange={(e: unknown) => dispatch(changePrompt((e as InputEventData).target?.value || ''))}
          placeholder="Ask a question about the graph"
          disabled={graphQuery?.isFetching}
          rows={4}
          ref={(el: HTMLTextAreaElement | null) => (promptInputRef.current = el)}
          onKeyDown={(e: unknown) => {
            const ke = e as KeyboardEventData
            if (ke.key === 'Escape') {
              closeModal()
            }
            if (ke.key === 'Enter' && !ke.shiftKey) {
              ke.preventDefault?.()
              confirmAsk()
            }
          }}
        />
      </div>
      <div className="modal-footer">
        <Button variant="primary" onClick={confirmAsk} disabled={graphQuery?.isFetching || !prompt?.trim()?.length}>
          Ask
        </Button>
        <Button onClick={closeModal} disabled={graphQuery?.isFetching}>
          Cancel
        </Button>
      </div>
    </Modal>
  )
}

export default GraphQueryModal
